import React, { useEffect, useState, useMemo } from "react";
import "./TradeEntryAttemptLogTable.css";
import { buildScopedApiUrl, ensureArray, getApiHeaders } from "./config";
import { useWorkspace } from "./workspace/WorkspaceContext";
import TradingViewChartIcon from "./TradingViewChartIcon";

const BASE_SERVICE_URL = "/api/bot/ui/get";
const DEFAULT_PAGE_SIZE = 10;
const GREEN_FLAG_COLUMNS = new Set(["breakEvenHit", "takeProfitHit", "trailingSlHit", "partialTpTaken"]);

const FLAG_COLUMNS = new Set([
  "positionOpened",
  "dcaTaken",
  "stopLossHit",
  "breakEvenHit",
  "takeProfitHit",
  "trailingSlHit",
  "partialTpTaken",
  "manualClose",
  "profitPercentBasedPTPTrackerPlaced",
  "tslLevelTrackerApplied",
  "breakEvenLevelTrackerApplied",
]);

const CATEGORICAL_COLUMNS = new Set([
  ...FLAG_COLUMNS,
  "tradeSide",
  "activeFlag",
  "exchange",
  "marketType",
  "symbol",
  "timeFrame",
  "ptpTimeFrame",
  "dcaTimeFrame",
  "tslSRLevelTimeFrame",
  "breakEvenSRTimeFrame",
]);

const NUMERIC_COLUMNS = new Set([
  "id",
  "tradeEntryTrackerId",
  "signalRequestId",
  "attemptNumber",
  "timeFrame",
  "ptpTimeFrame",
  "dcaTimeFrame",
  "tslSRLevelTimeFrame",
  "breakEvenSRTimeFrame",
]);

const COLUMNS = [
  { key: "id", label: "ID" },
  { key: "tradeEntryTrackerId", label: "Tracker ID" },
  { key: "signalRequestId", label: "Signal Request ID" },
  { key: "attemptNumber", label: "Attempt" },
  { key: "activeFlag", label: "Active" },
  { key: "symbol", label: "Symbol" },
  { key: "chart", label: "Chart" },
  { key: "timeFrame", label: "TF" },
  { key: "tradeSide", label: "Side" },
  { key: "dcaTaken", label: "DCA" },
  { key: "stopLossHit", label: "SL" },
  { key: "breakEvenHit", label: "BE" },
  { key: "partialTpTaken", label: "Partial TP" },
  { key: "takeProfitHit", label: "TP" },
  { key: "manualClose", label: "Manual Close" },
  { key: "trailingSlHit", label: "TSL Hit" },
  { key: "tslLevelTrackerApplied", label: "TSL Applied" },
  { key: "profitPercentBasedPTPTrackerPlaced", label: "PTP Tracker Placed" },
  { key: "breakEvenLevelTrackerApplied", label: "BE Tracker Applied" },
  { key: "tslLevel", label: "TSL Level" },
  { key: "tslSRLevelTimeFrame", label: "TSL TF" },
  { key: "hitProfitPct", label: "Profit%" },
  { key: "closeSizePercentage", label: "Close Size%" },
  { key: "dcaAddSizePercentage", label: "DCA Size%" },
  { key: "triggeredStopLossLevel", label: "Triggered SL" },
  { key: "ptpTimeFrame", label: "PTP TF" },
  { key: "dcaTimeFrame", label: "DCA TF" },
  { key: "breakEvenLevel", label: "BE Level" },
  { key: "breakEvenSRTimeFrame", label: "BE SR TF" },
  { key: "trailingSlMinProfitPct", label: "TSL Min Profit%" },
  { key: "breakEvenMinProfitPct", label: "BE Min Profit%" },
  { key: "partialTpMinProfitPct", label: "Partial TP Min Profit%" },
  { key: "dcaMinProfitPct", label: "DCA Min Profit%" },
  { key: "partialTpCloseSizePct", label: "Partial TP Close Size%" },
  { key: "dcaAddSizePct", label: "DCA Add Size%" },
  { key: "comments", label: "Comments" },
  { key: "createdDate", label: "Created Date" },
  { key: "createdBy", label: "Created By" },
  { key: "modifiedDate", label: "Modified Date" },
  { key: "modifiedBy", label: "Modified By" },
  { key: "exchange", label: "Exchange" },
  { key: "marketType", label: "Market Type" },
];

function formatValue(row, key) {
  const value = row?.[key];
  if (FLAG_COLUMNS.has(key)) {
    return value ? "Yes" : "No";
  }
  if (value === null || value === undefined || value === "") {
    return "-";
  }
  return String(value);
}

function TradeEntryAttemptLogTable({ closeModal }) {
  const { workspace } = useWorkspace();
  const [logs, setLogs] = useState([]);
  const [filters, setFilters] = useState({});
  const [sortConfig, setSortConfig] = useState({ key: "id", direction: "desc" });
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [cutoffDays, setCutoffDays] = useState(3);
  const [refreshVersion, setRefreshVersion] = useState(0);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState("");

  const filterOptions = useMemo(() => {
    const options = {};
    CATEGORICAL_COLUMNS.forEach((key) => {
      options[key] = [...new Set(
        logs
          .map((row) => formatValue(row, key))
          .filter((value) => value !== "-")
      )].sort((a, b) => NUMERIC_COLUMNS.has(key)
        ? Number(a) - Number(b)
        : a.localeCompare(b));
    });
    return options;
  }, [logs]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setFetchError("");
    setLogs([]);
    setPage(1);
    fetch(buildScopedApiUrl(`${BASE_SERVICE_URL}/getAllTradeEntryAttemptLogs`, workspace, { days: cutoffDays }), {
      headers: getApiHeaders(),
    })
      .then((res) => {
        if (!res.ok) throw new Error(`Request failed with status ${res.status}`);
        return res.json();
      })
      .then((data) => {
        if (cancelled) return;
        const normalizedLogs = ensureArray(data)
          .filter(Boolean)
          .map((row) => ({
            ...row,
            exchange: row.exchange || workspace.exchange,
            marketType: row.marketType || workspace.marketType,
          }));
        if (!Array.isArray(data)) {
          console.warn("Unexpected trade entry attempt log payload shape", workspace, data);
        }
        setLogs(normalizedLogs);
      })
      .catch((error) => {
        if (cancelled) return;
        console.error("Trade entry attempt log fetch failed:", error);
        setFetchError("Unable to fetch data. Please try again.");
        setLogs([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [workspace, cutoffDays, refreshVersion]);

  const filteredData = useMemo(() => {
    return logs.filter((row) =>
      COLUMNS.every(({ key }) => {
        const filterText = (filters[key] || "").trim().toLowerCase();
        if (!filterText) return true;
        return formatValue(row, key).toLowerCase().includes(filterText);
      })
    );
  }, [logs, filters]);

  const sortedData = useMemo(() => {
    const { key, direction } = sortConfig;
    const directionFactor = direction === "asc" ? 1 : -1;

    return [...filteredData].sort((a, b) => {
      let aVal = a[key] ?? "";
      let bVal = b[key] ?? "";

      if (NUMERIC_COLUMNS.has(key)) {
        aVal = Number(aVal) || 0;
        bVal = Number(bVal) || 0;
      } else {
        aVal = formatValue(a, key).toLowerCase();
        bVal = formatValue(b, key).toLowerCase();
      }

      if (aVal < bVal) return -1 * directionFactor;
      if (aVal > bVal) return 1 * directionFactor;
      return 0;
    });
  }, [filteredData, sortConfig]);

  const totalPages = pageSize === "all"
    ? 1
    : Math.max(1, Math.ceil(sortedData.length / pageSize));

  useEffect(() => {
    setPage((current) => Math.min(current, totalPages));
  }, [totalPages]);

  const pagedData = useMemo(() => {
    if (pageSize === "all") return sortedData;
    const start = (page - 1) * pageSize;
    return sortedData.slice(start, start + pageSize);
  }, [sortedData, page, pageSize]);

  const firstVisibleRecord = sortedData.length === 0 || pageSize === "all"
    ? (sortedData.length === 0 ? 0 : 1)
    : ((page - 1) * pageSize) + 1;
  const lastVisibleRecord = pageSize === "all"
    ? sortedData.length
    : Math.min(page * pageSize, sortedData.length);

  const handleSort = (key) => {
    setPage(1);
    setSortConfig((current) => ({
      key,
      direction: current.key === key && current.direction === "asc" ? "desc" : "asc",
    }));
  };

  const handleFilterChange = (key, value) => {
    setPage(1);
    setFilters((current) => ({
      ...current,
      [key]: value,
    }));
  };

  const clearFilters = () => {
    setFilters({});
    setPage(1);
  };

  const sortMarker = (key) => {
    if (sortConfig.key !== key) return "";
    return sortConfig.direction === "asc" ? " ^" : " v";
  };

  return (
    <div className="attempt-log-container">
      <div className="attempt-log-header attempt-log-header-centered">
        <span aria-hidden="true" />
        <h2>Trade Entry Attempt Logs</h2>
        <div className="attempt-log-header-actions">
        <button className="attempt-clear-filters-btn" onClick={clearFilters}>
          <span className="attempt-clear-filters-btn__icon" aria-hidden="true" />
          Clear Filters
        </button>
        {closeModal && <button type="button" className="panel-close-btn" onClick={closeModal} aria-label="Close Trade Entry Attempt Logs" title="Close Trade Entry Attempt Logs" />}
        </div>
      </div>

      <div className="toolbar">
        {[3, 7, 30].map((days) => (
          <button key={days} type="button" aria-pressed={cutoffDays === days}
            onClick={() => {
              setCutoffDays(days);
              setRefreshVersion((version) => version + 1);
            }}>
            Fetch {days} Days
          </button>
        ))}
        <span>Last {cutoffDays} days (Created Date)</span>
      </div>

      <div className="attempt-fetch-status" role="status" aria-live="polite">
        {loading ? "Fetching Data..." : fetchError}
      </div>
      <div className="table-wrapper" aria-busy={loading}>
        <table className="attempt-log-table">
          <thead>
            <tr>
              <th>S.No</th>
              {COLUMNS.map(({ key, label }) => (
                <th key={key} onClick={key === "chart" ? undefined : () => handleSort(key)}>
                  {label}
                  {sortMarker(key)}
                </th>
              ))}
            </tr>
            <tr className="attempt-filter-row">
              <th />
              {COLUMNS.map(({ key, label }) => (
                <th key={`${key}-filter`}>
                  {key === "chart" ? null : CATEGORICAL_COLUMNS.has(key) ? (
                    <select
                      aria-label={`Filter ${label}`}
                      value={filters[key] || ""}
                      onChange={(event) => handleFilterChange(key, event.target.value)}
                      onClick={(event) => event.stopPropagation()}
                    >
                      <option value="">All</option>
                      {(filterOptions[key] || []).map((option) => (
                        <option key={option} value={option}>{option}</option>
                      ))}
                    </select>
                  ) : (
                    <input
                      value={filters[key] || ""}
                      onChange={(event) => handleFilterChange(key, event.target.value)}
                      onClick={(event) => event.stopPropagation()}
                      placeholder={`Filter ${label}`}
                    />
                  )}
                </th>
              ))}
            </tr>
          </thead>

          <tbody>
            {pagedData.map((row, index) => (
              <tr key={row.id}>
                <td>{firstVisibleRecord + index}</td>
                {COLUMNS.map(({ key }) => {
                  if (key === "chart") {
                    return (
                      <td key={`${row.id}-${key}`}>
                        <TradingViewChartIcon symbol={row.symbol} timeFrame={row.timeFrame}
                          exchange={row.exchange} marketType={row.marketType} />
                      </td>
                    );
                  }
                  const isFlag = FLAG_COLUMNS.has(key);
                  const flagClass = !row[key] ? "attempt-flag-neutral"
                    : key === "stopLossHit" ? "attempt-flag-loss"
                      : key === "manualClose" ? "attempt-flag-manual"
                      : GREEN_FLAG_COLUMNS.has(key) ? "attempt-flag-profit" : "attempt-flag-neutral";
                  return (
                    <td
                      key={`${row.id}-${key}`}
                      className={isFlag ? flagClass : undefined}
                    >
                      {formatValue(row, key)}
                    </td>
                  );
                })}
              </tr>
            ))}
            {!loading && !fetchError && sortedData.length === 0 && (
              <tr>
                <td className="attempt-log-empty" colSpan={COLUMNS.length + 1}>
                  No trade entry attempt logs found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="attempt-pagination">
        <div className="attempt-page-size">
          <label htmlFor="attempt-page-size">Records per page</label>
          <select
            id="attempt-page-size"
            value={pageSize}
            onChange={(event) => {
              const value = event.target.value;
              setPageSize(value === "all" ? "all" : Number(value));
              setPage(1);
            }}
          >
            <option value={10}>10</option>
            <option value={20}>20</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
            <option value="all">All</option>
          </select>
        </div>

        <div className="attempt-page-navigation" aria-label="Trade entry attempt log pagination">
          <button
            type="button"
            disabled={page === 1 || pageSize === "all"}
            onClick={() => setPage((current) => Math.max(1, current - 1))}
          >
            Previous
          </button>
          <span>Page {page} of {totalPages}</span>
          <button
            type="button"
            disabled={page === totalPages || pageSize === "all"}
            onClick={() => setPage((current) => Math.min(totalPages, current + 1))}
          >
            Next
          </button>
        </div>

        <div className="attempt-record-total">
          Showing {firstVisibleRecord}-{lastVisibleRecord} of {sortedData.length} records
          {filteredData.length !== logs.length && ` (filtered from ${logs.length} total)`}
        </div>
      </div>
    </div>
  );
}

export default TradeEntryAttemptLogTable;
